import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'

export interface SearchableSelectOption {
  value: string
  label: string
  description?: string
  searchText?: string
}

export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder = 'Поиск',
  emptyMessage = 'Ничего не найдено',
  disabled = false,
  matchTriggerWidth = false,
}: {
  value: string
  options: SearchableSelectOption[]
  onChange: (value: string) => void
  placeholder: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  matchTriggerWidth?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  const selected = options.find(option => option.value === value) ?? null
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('ru')
    if (!normalized) return options

    return options.filter(option => [option.label, option.description, option.searchText]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('ru')
      .includes(normalized))
  }, [options, query])

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [])

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen(current => !current)
          if (open) setQuery('')
        }}
        className="input flex w-full items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
      >
        {/* Плейсхолдер тоже усекаем: без `min-w-0 truncate` его min-content
            распирал кнопку и вместе с ней всю грид-строку. `title` — чтобы
            усечённое длинное имя можно было прочитать наведением. */}
        <span
          className={`min-w-0 truncate ${selected ? '' : 'text-muted-foreground'}`}
          title={selected?.label ?? placeholder}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Ширина списка — `min(280px,100%)`, а не голые 280px: в узкой колонке
          список шире триггера вылезал за правый край контейнера, а
          `overflow-y:auto` родителя превращает это в горизонтальный скролл
          всей модалки. */}
      {open && !disabled && (
        <div className={`absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-border bg-card shadow-lg ${matchTriggerWidth ? 'min-w-0' : 'min-w-[min(280px,100%)]'}`}>
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md bg-muted/50 px-2 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                autoFocus
              />
            </div>
          </div>

          <div
            className="max-h-72 touch-pan-y overflow-y-auto overscroll-contain py-1"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {filtered.map(option => {
              const isSelected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                    setQuery('')
                  }}
                  title={option.description ? `${option.label} — ${option.description}` : option.label}
                  className={`block w-full px-3 py-2 text-left transition-colors ${
                    isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'
                  }`}
                >
                  <span className={`block truncate text-sm font-medium ${isSelected ? 'text-primary' : ''}`}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </button>
              )
            })}

            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">{emptyMessage}</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
